/**
 * useListCardOperations — quantity / priority / notes mutations for list rows.
 *
 * Mirrors `useDeckCardOperations`. Pending state is tracked **per row** so a single mutation
 * never disables the whole page.
 */
import { useCallback, useState } from 'react';

import type { ListPriority } from '@/types/lists';

import { useRemoveCardFromList, useUpdateListCard } from './api';

export interface ListCardOperations {
  increase: (cardId: string, currentQuantity: number) => Promise<void>;
  /** Removes the row when the current quantity is 1. */
  decrease: (cardId: string, currentQuantity: number) => Promise<void>;
  remove: (cardId: string) => Promise<void>;
  setPriority: (cardId: string, priority: ListPriority) => Promise<void>;
  setNotes: (cardId: string, notes: string | null) => Promise<void>;
  isPending: (cardId: string) => boolean;
}

export default function useListCardOperations(): ListCardOperations {
  const updateCard = useUpdateListCard();
  const removeCard = useRemoveCardFromList();
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());

  const track = useCallback(async (cardId: string, run: () => Promise<void>) => {
    setPendingIds((prev) => new Set(prev).add(cardId));
    try {
      await run();
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(cardId);
        return next;
      });
    }
  }, []);

  const increase = useCallback(
    (cardId: string, currentQuantity: number) => track(cardId, () => updateCard(cardId, { quantity: currentQuantity + 1 })),
    [track, updateCard],
  );

  const remove = useCallback((cardId: string) => track(cardId, () => removeCard(cardId)), [track, removeCard]);

  const decrease = useCallback(
    (cardId: string, currentQuantity: number) =>
      currentQuantity <= 1 ? remove(cardId) : track(cardId, () => updateCard(cardId, { quantity: currentQuantity - 1 })),
    [track, updateCard, remove],
  );

  const setPriority = useCallback(
    (cardId: string, priority: ListPriority) => track(cardId, () => updateCard(cardId, { priority })),
    [track, updateCard],
  );

  const setNotes = useCallback(
    (cardId: string, notes: string | null) => track(cardId, () => updateCard(cardId, { notes })),
    [track, updateCard],
  );

  const isPending = useCallback((cardId: string) => pendingIds.has(cardId), [pendingIds]);

  return { increase, decrease, remove, setPriority, setNotes, isPending };
}
